// react
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

// api
import {
  type CachedEnergySavingsOutput,
  type CachedInitiativeOutput,
  energyAnalysisOpenApi,
} from '@/api/generated/energyAnalysis/endpoints/energySavings';
import type { GetEnergyGroupDto } from '@/api/generated/recalc/endpoints/v2/energyGroups';

interface UseDeepDiveSavingsOptions {
  accountId: number | undefined;
  energyGroups: GetEnergyGroupDto[] | undefined;
}

interface UseDeepDiveSavingsResult {
  /** Estimated yearly savings in kWh per energy group, as shown on the ESG page. */
  savingsByGroupId: Record<number, number | null>;
  isLoading: boolean;
}

const getInitiativeSavings = (
  initiative: CachedInitiativeOutput,
): number | null => {
  const savings = initiative.AdvancedModelUsed
    ? initiative.ForecastedSavings?.AdvEstimatedYearlySavings
    : initiative.ForecastedSavings?.BasicEstimatedYearlySavings;

  return typeof savings === 'number' ? savings : null;
};

const getDeepDiveSavingsTotal = (
  energySavings: CachedEnergySavingsOutput,
): number | null => {
  const totals = energySavings.InitiativeEnergySavings.map(
    getInitiativeSavings,
  ).filter((value): value is number => typeof value === 'number');

  if (!totals.length) {
    return null;
  }

  return totals.reduce((sum, value) => sum + value, 0);
};

export const useDeepDiveSavings = ({
  accountId,
  energyGroups,
}: UseDeepDiveSavingsOptions): UseDeepDiveSavingsResult => {
  const dispatch = useDispatch();
  const [savingsByGroupId, setSavingsByGroupId] = useState<
    Record<number, number | null>
  >({});
  const [isLoading, setIsLoading] = useState(false);

  const groupIdsWithInitiatives = useMemo(
    () =>
      (energyGroups ?? [])
        .filter(
          (group) =>
            typeof group.energyGroupId === 'number' &&
            (group.energyInitiatives?.length ?? 0) > 0,
        )
        .map((group) => group.energyGroupId as number),
    [energyGroups],
  );

  useEffect(() => {
    if (!accountId || !groupIdsWithInitiatives.length) {
      setSavingsByGroupId({});
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    setIsLoading(true);

    const requests = groupIdsWithInitiatives.map((energyGroupId) => ({
      energyGroupId,
      request: dispatch(
        energyAnalysisOpenApi.endpoints.cachedEnergySavingsEnergySavingsAccountIdEnergyGroupIdGet.initiate(
          {
            accountId,
            energyGroupId,
          },
        ),
      ),
    }));

    void Promise.all(
      requests.map(async ({ energyGroupId, request }) => {
        try {
          const data = await request.unwrap();
          return [energyGroupId, getDeepDiveSavingsTotal(data)] as const;
        } catch {
          return [energyGroupId, null] as const;
        }
      }),
    ).then((entries) => {
      if (!isMounted) {
        return;
      }

      setSavingsByGroupId(Object.fromEntries(entries));
      setIsLoading(false);
    });

    return () => {
      isMounted = false;
      requests.forEach(({ request }) => {
        request.unsubscribe?.();
      });
    };
  }, [accountId, dispatch, groupIdsWithInitiatives]);

  return {
    savingsByGroupId,
    isLoading,
  };
};
